/* global dataLayer */
import React, { Fragment, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useForm, useWatch } from 'react-hook-form';
import { ErrorMessage } from '@hookform/error-message';
import { useStateMachine } from 'little-state-machine';
import { useRouter } from 'next/router';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import axios from 'axios';
import _ from 'lodash';
import Spar from 'Components/Spar';
import PersonalDataFields from 'Components/PersonalDataFields';
import Fieldset from 'Components/Fieldset';
import FieldRow from 'Components/FieldRow';
import Field from 'Components/Field';
import Loader from 'Components/Loader';
import ButtonNext from 'Components/ButtonNext';
import checkPhone from 'utils/checkPhone';
import checkSsn from 'utils/checkSsn';
import formatSsn from 'utils/formatSsn';
import updateAction from 'utils/updateAction';
import s from './Step4.module.scss';

const Step4 = ({ formNamespace, countries, texts }) => {
    const router = useRouter();
    const updateName = (state, payload) => {
        return updateAction(state, payload, formNamespace);
    };
    const { state, actions } = useStateMachine({ updateName });

    const currState = _.get(state, formNamespace, {});
    const defaultManualInput = _.get(currState, 'manualInput', false);
    const {spar} = currState;

    const [manualInput, useManualInput] = useState(defaultManualInput);
    const [loading, setLoading] = useState(false);
    const [sparError, setSparError] = useState(false);

    const fetchSpar = () => {
        const ssn = formatSsn(getValues('ssn'));
        setLoading(true);
        setSparError(false);

        axios.get(`/wp-json/spar/v1/obfuscate/ssn/?ssn=${ssn}`).then((response) => {
            actions.updateName({
                spar: response.data,
                sparId: response.data.spar_id,
                ssn: ssn,
            });
            setValue('sparId', response.data.spar_id);
            trigger('sparId');
            setLoading(false);
        }).catch((err) => {
            console.error('error', err);
            setSparError(true);
            setLoading(false);
        });
    };

    let schema = yup.object().shape({
        ssn: yup
            .string()
            .test(
                'validSsn',
                'Vänligen fyll i ett giltigt personnummer: ÅÅÅÅMMDDNNNN',
                (value) => checkSsn(value, false)
            ),
        email: yup
            .string()
            .email('Vänligen fyll i en giltig e-postadress')
            .required('Vänligen fyll i en giltig e-postadress'),
        phone: yup
            .string()
            .test(
                'validPhone',
                'Vänligen fyll i ett giltigt telefonnummer: +46 73 123 45 67',
                checkPhone
            ),
        sparId: yup
            .string()
            .required(
                'Vänligen fyll till dina personuppgifter manuellt eller hämta uppgifter med ditt personnummer'
            ),
    });

    if (manualInput) {
        schema = schema.shape({
            firstName: yup.string().required('Vänligen fyll i ditt förnamn'),
            lastName: yup.string().required('Vänligen fyll i ditt efternamn'),
            address: yup.string().required('Vänligen fyll i din adress'),
            postalCode: yup.string().required('Vänligen fyll i ditt postnummer'),
            city: yup.string().required('Vänligen fyll i en giltig postort'),
            country: yup.object()
                .shape({
                    value: yup.string(),
                    label: yup.string(),
                })
                .nullable()
                .required('Vänligen välj ett alternativ'),
            sparId: null,
        });
    }

    const {
        register,
        handleSubmit,
        getValues,
        setValue,
        formState,
        trigger,
        control,
    } = useForm({
        mode: 'onChange',
        resolver: yupResolver(schema),
        defaultValues: currState,
    });
    const { errors, isValid } = formState;

    const watchSsn = useWatch({control, name: 'ssn'});

    useEffect(() => {
        if(watchSsn) {
            checkSsn(watchSsn, false).then((valid) => {
                if (valid && !manualInput) {
                    fetchSpar();
                } else {
                    setValue('sparId', null),
                    actions.updateName({ sparId: null });
                    actions.updateName({ spar: null });
                }
            });
        }
    }, [watchSsn]);

    const onSubmit = (data) => {
        dataLayer.push({
            event: 'checkoutSteps',
            eventCategory: 'hyllningsgåva',
            eventAction: 'personuppgifter',
        });

        data.ssn = data.ssn ? formatSsn(data.ssn) : '';

        Object.assign(data, { steg4: true });
        actions.updateName(data);
        router.push({hash: 'steg5'});
    };

    /* eslint-disable react-hooks/rules-of-hooks */
    return (
        <form className={s['Step4']} onSubmit={handleSubmit(onSubmit)}>
            <Fieldset title="Fyll i dina uppgifter">
                <div className={s['Step4__SsnFieldContainer']}>
                    <Field
                        name="ssn"
                        label="Ditt personnummer"
                        placeholder="ÅÅÅÅMMDDNNNN"
                        register={register('ssn')}
                        errors={errors}
                    />

                    <button
                        className={s['Step4__SparButton']}
                        type="button"
                        onClick={() => [useManualInput(false), fetchSpar()]}
                        disabled={!getValues('ssn') || 'ssn' in errors}>
                        Hämta adress
                    </button>
                </div>

                {manualInput && (
                    <PersonalDataFields
                        register={register}
                        control={control}
                        errors={errors}
                        countries={countries}
                    />
                )}

                {!manualInput && (
                    <Fragment>
                        {!loading && sparError &&
                            <div className={s['Step4__SparError']} role="alert">
                                Något gick fel vid hämtning av din adress, vänligen ange adress manuellt istället
                            </div>
                        }
                        <div className={s['Step4__ManualInputButtonContainer']}>
                            <button
                                type="button"
                                className={s['Step4__ManualInputButton']}
                                onClick={() => [
                                    useManualInput(true),
                                    setValue('sparId', null),
                                    actions.updateName({ manualInput: true }),
                                    actions.updateName({ sparId: null }),
                                    actions.updateName({ spar: null }),
                                ]}>
                                Fyll i personuppgifter manuellt
                            </button>
                            <ErrorMessage
                                errors={errors}
                                name="sparId"
                                as={<p className={s['Step4__SparError']} />}
                            />
                        </div>
                        {spar && !loading && getValues('ssn') !== '' && (
                            <Spar
                                spar={spar}
                                gdprText={texts.gdprText}
                            />
                        )}
                        {loading && getValues('ssn') !== '' && (
                            <div className={s['Step4__SparLoader']}>
                                <Loader size={100} />
                            </div>
                        )}
                        <input type="hidden" name="sparId" {...register} />
                    </Fragment>
                )}

                <Field
                    name="email"
                    label="Din e-post"
                    placeholder=""
                    required={true}
                    register={register('email')}
                    errors={errors}
                />

                <FieldRow>
                    <Field
                        name="phone"
                        label="Ditt mobilnummer"
                        placeholder="+46 73 123 45 67"
                        required={true}
                        register={register('phone')}
                        errors={errors}
                        width="Full"
                    />
                    <Field
                        name="phoneText"
                        label="Annan telefon"
                        placeholder="+46 73 123 45 67"
                        tooltip="Eventuellt annat telefonnummer som vi kan nå dig på."
                        register={register('phoneText')}
                        errors={errors}
                        width="Full"
                    />
                </FieldRow>
            </Fieldset>

            <ButtonNext disabled={!isValid}>Nästa steg</ButtonNext>
        </form>
    );
    /* eslint-enable react-hooks/rules-of-hooks */
};

Step4.propTypes = {
    formNamespace: PropTypes.string,
    countries: PropTypes.array,
    texts: PropTypes.object,
};


export default Step4;
